import { useEffect, useState } from 'react';

import { apiClient } from '@/services/legacy-api';
import type { TeamMember } from '@/types';

// Module-level once-per-session cache: every claim/assign menu shares one
// roster and the preview pane remounts constantly — refetching the same
// list per selection was measurable noise.
let rosterPromise: Promise<TeamMember[]> | null = null;

/** Test hook — clears the module cache between renders. */
export function clearAnalystRosterCache(): void {
  rosterPromise = null;
}

/**
 * The claim/assign candidate list: every active administrator, regardless
 * of team. Deliberately NOT team-scoped — cases are assigned between
 * administrators, and production admin accounts sit on assorted team rows
 * (or none), so a per-team roster collapsed to just the caller.
 *
 * `enabled` false reads nothing and returns the empty roster: for a
 * surface that only needs a name once a colleague's item is on screen.
 */
export function useAnalystRoster(enabled = true): TeamMember[] {
  const [members, setMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    if (!rosterPromise) {
      rosterPromise = apiClient.getAdministrators().catch((err) => {
        // Don't cache failures — the next mount retries.
        rosterPromise = null;
        throw err;
      });
    }
    rosterPromise
      .then((list) => {
        if (!cancelled) {
          setMembers(list);
        }
      })
      .catch(() => {
        // Menus degrade to claim/release-only; nothing to surface.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return members;
}
